import * as React from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { TabsWithUrl, TabMode } from '@common/react/components/UI/TabsWithUrl/TabsWithUrl';

import { PriceType } from '@app/objects/Price';
import { getServiceTypeName, ServiceType } from '@app/objects/Pet';
import { UserRole } from '@app/objects/User';
import { ApplicationState } from '@app/store';
import { useCrematory } from '@app/hooks/Crematory/useCrematory';
import { useCountry } from '@app/hooks/useCountry';
import { australiaCode } from '@app/components/UI/Inputs/PhoneInput';
import { useServiceTypes, ServiceTypeOption } from '@app/hooks/Crematory/useServiceTypes';

interface OwnProps {
	urlPrefix?: string;
	children: (data: SingleTableOptions) => React.ReactElement;
}

export interface SingleTableOptions {
	price: PriceType;
	service: ServiceType;
	title: string;
}

interface SingleTableProps {
	name: string;
	urlPrefix?: string;
	priceType: PriceType;
	children: (data: SingleTableOptions) => JSX.Element;
	fullWidth?: boolean;
}

export function useTableOptions(priceType: PriceType): Array<SingleTableOptions> {
	const options = useServiceTypes();

	return options.map((option: ServiceTypeOption) => ({
		price: priceType,
		service: option.value,
		title: option.label,
	}));
}

export const toQueryString = (str: string) => str.toLowerCase().replace(/\s/g, '');

export const SingleTable: React.FC<SingleTableProps> = ({
	urlPrefix, name, priceType, children, fullWidth,
}) => {
	const { t } = useTranslation();
	const options = useTableOptions(priceType);
	const crematory = useCrematory();
	const country = useCountry(crematory?.country);
	const isAustralia = React.useMemo(() => country?.phoneCode === australiaCode, [country]);

	const items = React.useMemo(() => (options.map((option) => ({
		key: toQueryString(option?.title),
		label: getServiceTypeName(option.service, t, false, isAustralia),
		children: children(option),
	}))), [options]);

	if (!options.length) return null;

	return (
		<div className={clsx('row-tabs', fullWidth ? 'card__body' : 'col-sm-6')}>
			<div className="site-subheadline" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
				<h1>{name}</h1>
			</div>
			<TabsWithUrl
				mode={TabMode.Query}
				tabsUrlKey={urlPrefix ? `${urlPrefix}-${name.toLowerCase()}` : name.toLowerCase()}
				defaultKey={toQueryString(options[0]?.title)}
				items={items}
			/>
		</div>
	);
};

export const CrematoryTables: React.FC<OwnProps> = ({ children, urlPrefix }: OwnProps) => {
	const role = useSelector((state: ApplicationState) => state.login.user?.role);

	return (
		<div className={clsx('row prices-tables', role === UserRole.Crematory && 'table--margin')}>
			<SingleTable name="Wholesale" urlPrefix={urlPrefix} priceType={PriceType.Wholesale}>{children}</SingleTable>
			<SingleTable name="Retail" urlPrefix={urlPrefix} priceType={PriceType.Retail}>{children}</SingleTable>
		</div>
	);
};
